import React from 'react'
import { useStyles } from './ContractListingV2.styles'
import { type ChangedContractInfo } from './ContractListingV2'
import { BuyerLogo } from '../../../common/components/BuyerLogo/BuyerLogo'
import {
  ModalConfirmV2,
  ModalSizeVariants,
  TableComponentV2,
} from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileCheck } from '@fortawesome/pro-regular-svg-icons'
import clsx from 'clsx'
import { TableCell } from '@mui/material'
import { getFormattedValue } from '@labrav/common'
import { formatToDecimalPlaces } from '../../../common/utils/meteringHelpers'

export interface ContractListingV2ModalProps {
  openModal: boolean
  handleSubmit: () => void
  handleCloseModal: () => void
  changedContracts: {
    [contractId: string]: ChangedContractInfo
  }
  count: number
  totalAmount: number | null
}

const mapping = {
  Buyer: 'buyerDetail',
  'Total amount': 'total',
}

export const ContractListingV2Modal: React.FC<ContractListingV2ModalProps> = ({
  openModal,
  handleSubmit,
  handleCloseModal,
  changedContracts,
  count,
  totalAmount,
}) => {
  const { classes } = useStyles()

  const customHeader = {
    buyerDetail: (
      <TableCell className={clsx(classes.tableCell, classes.buyerDetail)}>
        <div className={classes.modalTableHeading}>Buyer</div>
      </TableCell>
    ),
    total: (
      <TableCell className={classes.totalTableCell}>
        <div className={classes.modalTableHeading}>Total amount</div>
      </TableCell>
    ),
  }

  const customObject = Object.values(changedContracts).map(
    (changedContract: ChangedContractInfo, index: number) => {
      return {
        buyerDetail: (
          <div className={classes.buyerDetailContainer}>
            <BuyerLogo
              logoUrl={changedContract.buyerLogoUrl}
              classes={classes}
            />
            <div className={classes.tableValues}>
              {changedContract.buyerIdentifier}
            </div>
          </div>
        ),
        total: (
          <div className={clsx(classes.amountContainer, classes.buyerTotal)}>
            {changedContract.totalAmount === null ? (
              <div>-</div>
            ) : (
              <>
                <div className={classes.dollar}>$</div>
                <div className={classes.amount}>
                  {getFormattedValue(changedContract.totalAmount)}
                </div>
              </>
            )}
          </div>
        ),
      }
    }
  )

  return (
    <div data-testid="submit-metering-record-modal">
      <ModalConfirmV2
        title={'Submit metering record'}
        icon={
          <FontAwesomeIcon
            icon={faFileCheck}
            data-testid="save-metering-record-icon-modal"
          />
        }
        hideAcceptButton={false}
        hideCancelButton={false}
        content={
          <div className={classes.content}>
            <div className={classes.totalValue}>
              This step cannot be undone. Are you sure you want to proceed?
            </div>
            <div className={classes.tableWrapper}>
              <TableComponentV2
                keyName={'buyers-data-table'}
                mapping={mapping}
                customHeader={customHeader || []}
                objects={customObject || []}
                showCheckbox={false}
                hasMoreData={false}
                fetchMoreData={() => {}}
                selectedItem={[]}
                onChangeCheckbox={() => {}}
                isLoading={false}
                tableHeight={count > 5 ? '340px' : 'auto'}
              />
            </div>
          </div>
        }
        open={openModal}
        titleAcceptButton={'Submit'}
        onAccept={handleSubmit}
        onDecline={handleCloseModal}
        titleCancelButton={'Discard'}
        footerActions={
          <div className={classes.footerActions}>
            <div className={classes.totalText}>
              <span className={classes.totalValue}>
                Total amount to be metered for
              </span>{' '}
              <span className={classes.amount}>{count}</span>{' '}
              <span className={classes.totalValue}>
                {count > 1 ? 'buyers:' : 'buyer:'}
              </span>{' '}
              <span>
                {totalAmount === null ? (
                  <span className={classes.totalValue}>-</span>
                ) : (
                  <>
                    <span className={classes.dollar}>$</span>
                    <span className={classes.amount}>
                      {formatToDecimalPlaces(totalAmount, 3)}
                    </span>
                  </>
                )}
              </span>
            </div>
          </div>
        }
        variant={ModalSizeVariants.large}
      />
    </div>
  )
}
